"use client";

import Image from "next/image";
import { useCallback, useEffect, useState } from "react";
import { useLanguage } from "@/components/LanguageProvider";
import { resolveApiAssetUrl } from "@/lib/backend";
import type { GalleryItem } from "@/lib/types";

type Props = { items: GalleryItem[] };

export function GalleryContent({ items }: Props) {
  const { t, dir, lang } = useLanguage();
  const [active, setActive] = useState<GalleryItem | null>(null);

  const close = useCallback(() => setActive(null), []);

  useEffect(() => {
    if (!active) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") close();
    };
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [active, close]);

  return (
    <div dir={dir}>
      <header className="border-b border-slate-200/80 bg-gradient-to-br from-white via-slate-50 to-[#f0f9ff] px-4 py-14 text-center md:py-20">
        <p className="text-xs font-bold uppercase tracking-widest text-[var(--accent)]">
          {t("gallery.badge")}
        </p>
        <h1 className="mt-2 text-4xl font-extrabold text-[var(--primary)]">
          {t("gallery.title")}
        </h1>
        <p className="mx-auto mt-4 max-w-2xl text-slate-600">{t("gallery.subtitle")}</p>
      </header>

      <div className="mx-auto max-w-6xl px-4 py-14 md:px-6 md:py-20">
        {items.length === 0 ? (
          <p className="rounded-2xl border border-dashed border-slate-300 bg-white py-16 text-center text-slate-500">
            {t("gallery.empty")}
          </p>
        ) : (
          <ul className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {items.map((item) => {
              const caption =
                lang === "ar" && item.captionAr ? item.captionAr : item.caption;
              const alt =
                item.imageAlt ||
                caption ||
                t("gallery.viewImage");
              const src = resolveApiAssetUrl(item.imageUrl);
              return (
                <li key={item.id}>
                  <button
                    type="button"
                    onClick={() => setActive(item)}
                    className="group relative block w-full overflow-hidden rounded-2xl border border-slate-200 bg-white text-left shadow-sm transition hover:-translate-y-0.5 hover:border-[var(--accent)]/40 hover:shadow-md focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--accent)]"
                    aria-label={`${t("gallery.viewImage")}${caption ? `: ${caption}` : ""}`}
                  >
                    <div className="relative aspect-[4/3] w-full bg-slate-100">
                      <Image
                        src={src}
                        alt={alt}
                        fill
                        className="object-cover transition duration-300 group-hover:scale-[1.02]"
                        sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw"
                      />
                    </div>
                    {caption ? (
                      <p className="border-t border-slate-100 px-4 py-3 text-sm font-medium text-[var(--primary)]">
                        {caption}
                      </p>
                    ) : null}
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {active ? (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/80 p-4 backdrop-blur-sm"
          role="dialog"
          aria-modal="true"
          aria-label={t("gallery.viewImage")}
          onClick={close}
        >
          <button
            type="button"
            className="absolute right-4 top-4 rounded-full bg-white/10 px-4 py-2 text-sm font-medium text-white hover:bg-white/20 focus-visible:outline focus-visible:outline-2 focus-visible:outline-white"
            onClick={close}
          >
            {t("gallery.close")}
          </button>
          <figure
            className="relative max-h-[85vh] max-w-5xl overflow-hidden rounded-2xl bg-white shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="relative max-h-[70vh] w-[min(90vw,56rem)]">
              <Image
                src={resolveApiAssetUrl(active.imageUrl)}
                alt={
                  active.imageAlt ||
                  (lang === "ar" && active.captionAr ? active.captionAr : active.caption) ||
                  t("gallery.viewImage")
                }
                width={1200}
                height={800}
                className="h-auto max-h-[70vh] w-full object-contain"
              />
            </div>
            {(lang === "ar" && active.captionAr ? active.captionAr : active.caption) ? (
              <figcaption className="border-t border-slate-100 px-6 py-4 text-center text-sm text-slate-700">
                {lang === "ar" && active.captionAr ? active.captionAr : active.caption}
              </figcaption>
            ) : null}
          </figure>
        </div>
      ) : null}
    </div>
  );
}
